import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { formatPhoneForDisplay } from '@shared/phone.ts';
import { isTerminalCallStatus } from '@shared/voice.ts';
import { Alert, Button, LoadingBlock, TextInput } from './ui.tsx';

/** The server answered and placed nothing. A timeout, a 5xx or "still starting" (409) may have placed one. */
const refusedOutright = (status: number) =>
  status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429;

/**
 * The key of each call the server has not answered for certain (no answer
 * yet, a 5xx, "still starting"), by customer, customer number and the phone it
 * rings. Kept outside the panel, so closing it while a call starts and trying
 * again finds that call rather than placing a second; a call that differs in
 * any of those is another call, with a key of its own, and a key is dropped
 * once the server has answered.
 */
const uncertainCalls = new Map<string, string>();

/**
 * "Call from the company number" on a customer (click-to-call): BlastoClean
 * rings the person's own phone first; when they answer and press 1, it calls
 * the customer, who sees the company's number (services/outbound-calls.ts).
 *
 * The phone rung first starts as the person's own (or the company's
 * forwarding phone) and can be changed for this call. Late where the customer
 * is, the panel says so before the call starts: calls placed by hand are not
 * held for quiet hours, but nobody should be surprised by the hour.
 */
export default function CustomerCallPanel({
  customerId,
  customerName,
  onClose,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const options = useQuery({
    queryKey: ['customer-call-options', customerId],
    queryFn: () => customerApi.callOptions(customerId),
  });
  // Null means "not edited": the suggested phone is shown.
  const [ringPhone, setRingPhone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const refreshThread = () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['conversation-thread'] });
  };

  const start = useMutation({
    // `call`: the customer, their number and the phone rung, which the key is kept by.
    mutationFn: ({ phone, call }: { phone: string; call: string }) => {
      let key = uncertainCalls.get(call);
      if (!key) {
        key = crypto.randomUUID();
        uncertainCalls.set(call, key);
      }
      return customerApi.startCall(customerId, phone, key);
    },
    onSuccess: (_started, { call }) => {
      uncertainCalls.delete(call);
      setError(null);
      setFieldError(undefined);
      // The call is on the thread already.
      refreshThread();
    },
    onError: (caught, { call }) => {
      // Not refused outright: the call may be on the thread all the same.
      if (!(caught instanceof ApiRequestError) || !refusedOutright(caught.status)) refreshThread();
      if (caught instanceof ApiRequestError) {
        if (refusedOutright(caught.status)) uncertainCalls.delete(call);
        setFieldError(caught.fields.ringPhone);
        setError(caught.fields.ringPhone ? null : caught.message);
      } else {
        setError(t('customerCall.startError'));
      }
    },
  });

  // A retried click can find its call over already (it rang out, or the
  // person hung up): that is said, and the next click is a new call.
  const ended = start.isSuccess && isTerminalCallStatus(start.data.status);
  const ringing = start.isSuccess && !ended;
  const data = options.data;
  const phone = ringPhone ?? data?.ringPhone ?? '';
  const display = (value: string | null) => (value ? formatPhoneForDisplay(value) : '');
  const localTime = data
    ? new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit', timeZone: data.timezone }).format(
        new Date(),
      )
    : '';

  return (
    <section
      className="mb-6 rounded border border-[color:var(--color-line)] p-4"
      aria-labelledby="customer-call-heading"
      data-testid="customer-call-panel"
    >
      <h2 id="customer-call-heading" className="mb-2 text-base font-semibold">
        {t('customerCall.heading', { name: customerName })}
      </h2>
      {options.isLoading ? <LoadingBlock label={t('customerCall.loading')} /> : null}
      {options.isError ? <Alert tone="error">{t('customerCall.loadError')}</Alert> : null}
      {data?.unavailableReason ? (
        <Alert tone="info">
          {data.unavailableReason === 'no_phone'
            ? t('customerCall.noPhone')
            : data.unavailableReason === 'no_line'
              ? t('customerCall.noLine')
              : t('customerCall.unavailable')}
        </Alert>
      ) : null}
      {data && !data.unavailableReason ? (
        ringing ? (
          <Alert tone="success">{t('customerCall.ringing', { phone: display(start.data!.ringPhone) })}</Alert>
        ) : (
          <>
            {ended ? (
              <div className="mb-3">
                <Alert tone="info">{t('customerCall.ended')}</Alert>
              </div>
            ) : null}
            <p className="mb-3 text-sm">
              {t('customerCall.howItWorks', {
                phone: display(phone) || t('customerCall.yourPhone'),
                name: customerName,
                line: display(data.line),
              })}
            </p>
            {data.quietHours ? (
              <div className="mb-3">
                <Alert tone="warning">{t('customerCall.late', { time: localTime, name: customerName })}</Alert>
              </div>
            ) : null}
            {error ? (
              <div className="mb-3">
                <Alert tone="error">{error}</Alert>
              </div>
            ) : null}
            <div className="grid max-w-md gap-1">
              <TextInput
                label={t('customerCall.ringPhoneLabel')}
                type="tel"
                autoComplete="tel"
                hint={t('customerCall.ringPhoneHint')}
                value={phone}
                error={fieldError}
                onChange={(event) => setRingPhone(event.target.value)}
              />
            </div>
          </>
        )
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {data && !data.unavailableReason && !ringing ? (
          <Button
            type="button"
            variant="primary"
            busy={start.isPending}
            disabled={!phone.trim()}
            onClick={() =>
              start.mutate({
                phone: phone.trim(),
                call: [customerId, data.customerPhone ?? '', phone.trim()].join('|'),
              })
            }
          >
            {t('customerCall.start')}
          </Button>
        ) : null}
        <Button type="button" onClick={onClose}>
          {ringing ? t('customerCall.done') : t('common.cancel')}
        </Button>
      </div>
    </section>
  );
}
